import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { runSnapshots } from "@/api/runs";
import { EmptyState } from "@/components/empty-state";
import { useRunSnapshots } from "@/hooks/useEventStream";
import { RUN_COLORS } from "@/lib/run-colors";

// Stack-key → color from the shared palette. `skipped` collapses both
// "tagged" and "no match" (snapshots don't carry the split, only the
// outcome bar does); the lighter grey reads as a generic "skipped".
//
// `extracted` uses the muted `duplicate` green rather than the saturated
// `pushed` green: the snapshot's `by_status.extracted` is the *total*
// (pushed + duplicate), and the bright pushed-line overlay sits on top.
// Painting the band muted means the saturated line reads as the
// "actually pushed" slice within the broader extracted area.
// Pending is intentionally omitted — its jitter from worker-pull timing
// made the stacked area read as "spiky" without telling the operator
// anything they can't see in the live outcome bar.
const TONES: Record<string, string> = {
  extracted: RUN_COLORS.duplicate,
  skipped: RUN_COLORS.noMatch,
  unreachable: RUN_COLORS.unreachable,
  error: RUN_COLORS.error,
};

const STACK_KEYS = [
  "extracted",
  "skipped",
  "unreachable",
  "error",
] as const;

export function ProgressChart({
  runId,
  height = 280,
}: {
  runId: string;
  height?: number;
}) {
  // One-shot backfill of historical snapshots; the SSE `snapshot` event
  // appends new rows live, so no polling.
  const snaps = useQuery({
    queryKey: ["snapshots", runId],
    queryFn: () => runSnapshots(runId),
  });
  const live = useRunSnapshots(runId);

  const data = useMemo(() => {
    const seen = new Set<string>();
    const merged = [...(snaps.data ?? []), ...live];
    const deduped = merged.filter((s) => {
      if (seen.has(s.at)) return false;
      seen.add(s.at);
      return true;
    });
    deduped.sort((a, b) => a.at.localeCompare(b.at));
    return deduped.map((s) => {
      const out: Record<string, number | string> = {
        ts: new Date(s.at).getTime(),
        close_pushed: s.close_pushed,
      };
      for (const k of STACK_KEYS) out[k] = s.by_status[k] ?? 0;
      return out;
    });
  }, [snaps.data, live]);

  if (snaps.isLoading) {
    return <div className="h-[280px] grid place-items-center text-sm text-muted">Loading chart…</div>;
  }
  if (data.length === 0) {
    return (
      <EmptyState
        title="No snapshots yet"
        hint="The worker writes one sample per minute while a run is active."
      />
    );
  }

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(t) =>
              new Date(t as number).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            }
            stroke="hsl(220 9% 46%)"
            fontSize={10}
            tick={{ fill: "hsl(220 9% 46%)" }}
          />
          <YAxis
            stroke="hsl(220 9% 46%)"
            fontSize={10}
            tick={{ fill: "hsl(220 9% 46%)" }}
            width={48}
            tickFormatter={(v) => Number(v).toLocaleString()}
          />
          <Tooltip
            contentStyle={{
              background: "hsl(222 47% 9%)",
              border: "1px solid hsl(217 33% 18%)",
              borderRadius: 6,
              fontSize: 12,
            }}
            labelFormatter={(t) => new Date(t as number).toLocaleString()}
            formatter={(v: number, key: string) => [v.toLocaleString(), key]}
          />
          {STACK_KEYS.map((k) => (
            <Area
              key={k}
              type="monotone"
              dataKey={k}
              stackId="status"
              stroke={TONES[k]}
              fill={TONES[k]}
              fillOpacity={0.45}
              isAnimationActive={false}
            />
          ))}
          <Line
            type="monotone"
            dataKey="close_pushed"
            stroke={RUN_COLORS.pushedLine}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// re-export so unused-import linter on AreaChart/LineChart doesn't fire if
// recharts tree-shakes; ComposedChart subsumes both visually.
export { AreaChart, LineChart };
